import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UnsavedChangesContext } from './unsavedChangesContext';

// Holds the "is anything dirty" flag for the whole app. See unsavedChangesContext.ts for why this exists;
// the short version is that changing the org/brand/location scope is not a route change, so the router's
// blocker cannot guard it and the scope selector has to ask for itself.
//
// Registration is keyed so two dirty screens cannot cancel each other out — the last one to go clean would
// otherwise clear the flag while the other still had edits pending.
export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dirtyKeys = useRef(new Set<string>());
    const [isDirty, setIsDirty] = useState(false);

    const setDirty = useCallback((key: string, dirty: boolean) => {
        if (dirty) dirtyKeys.current.add(key);
        else dirtyKeys.current.delete(key);

        // Only re-render when the overall answer actually flips.
        setIsDirty((current) => {
            const next = dirtyKeys.current.size > 0;
            return next === current ? current : next;
        });
    }, []);

    // How each guarded screen leaves itself, keyed the same way as the dirty flags.
    const discardHandlers = useRef(new Map<string, () => void>());
    const discarding = useRef(false);

    const registerDiscard = useCallback((key: string, handler: (() => void) | undefined) => {
        if (handler) discardHandlers.current.set(key, handler);
        else discardHandlers.current.delete(key);
    }, []);

    const isDiscarding = useCallback(() => discarding.current, []);

    const discard = useCallback(() => {
        // Raised ONLY when something is actually pending, and set BEFORE the handlers run: they navigate, and
        // the router guard must already know the question has been answered or it asks it again on the way
        // out.
        //
        // Raising it with nothing dirty would strand it. The effect below clears it when `isDirty` CHANGES to
        // false — if it was already false there is no change to react to, so the flag would stay up and the
        // unsaved-changes guard would be switched off for the rest of the session. The flag is not needed in
        // that case anyway: the guard only blocks when something is dirty.
        if (isDirty) discarding.current = true;
        discardHandlers.current.forEach((handler) => handler());
    }, [isDirty]);

    // Cleared by the discard finishing rather than by a timer. A discarded screen unmounts, which clears its
    // dirty flag, so nothing pending means the navigation this authorised is done. Timing this instead is
    // what would make it fragile.
    useEffect(() => {
        if (!isDirty) discarding.current = false;
    }, [isDirty]);

    const value = useMemo(
        () => ({ isDirty, setDirty, discard, registerDiscard, isDiscarding }),
        [isDirty, setDirty, discard, registerDiscard, isDiscarding]
    );

    return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
};
